import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { FaEdit, FaFlag, FaPlus, FaTimes, FaTrash } from 'react-icons/fa';

import { useGlobalFilters } from '../hooks/useGlobalFilters';
import { timelineEventsStore } from '../stores';
import { formatRuDate } from '../utils/ruDateUtils';


const EMPTY_FORM = { title: '', date: '', description: '' };

const EventList = observer(({ title, events, selectedIds, onToggle, onEdit, onDelete }) => (
  <section className="timeline-events-panel__section">
    <h4>{title}</h4>
    {events.length === 0 ? (
      <div className="timeline-events-panel__empty">Событий пока нет</div>
    ) : (
      events.map((event) => (
        <div className="timeline-events-panel__item" key={event.id}>
          <label className="timeline-events-panel__check">
            <input
              type="checkbox"
              checked={selectedIds.includes(event.id)}
              onChange={() => onToggle(event.id)}
            />
            <span>
              <strong>{formatRuDate(event.date)}</strong> — {event.title}
              {!event.is_owner && event.owner_username ? (
                <small>Автор: {event.owner_username}</small>
              ) : null}
              {event.description ? <small>{event.description}</small> : null}
            </span>
          </label>
          {event.is_owner ? (
            <div className="timeline-events-panel__actions">
              <button type="button" title="Изменить" onClick={() => onEdit(event)}>
                <FaEdit />
              </button>
              <button type="button" title="Удалить" onClick={() => onDelete(event)}>
                <FaTrash />
              </button>
            </div>
          ) : null}
        </div>
      ))
    )}
  </section>
));

const TimelineEventsPanel = observer(() => {
  const filtersStore = useGlobalFilters();
  const [open, setOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const eventsVersion = timelineEventsStore.events.map((event) => event.id).join(',');
  const selectedIdsVersion = filtersStore.selectedTimelineEventIds.join(',');

  useEffect(() => {
    timelineEventsStore.load().catch(() => {});
  }, []);

  useEffect(() => {
    const existingIds = new Set(timelineEventsStore.events.map((event) => event.id));
    const validIds = filtersStore.selectedTimelineEventIds.filter((id) => existingIds.has(id));
    if (
      timelineEventsStore.loaded &&
      validIds.length !== filtersStore.selectedTimelineEventIds.length
    ) {
      filtersStore.setSelectedTimelineEventIds(validIds);
    }
  }, [eventsVersion, selectedIdsVersion, filtersStore]);

  const selectedIds = new Set(filtersStore.selectedTimelineEventIds);
  const selectedEvents = timelineEventsStore.events.filter((event) => selectedIds.has(event.id));

  const resetForm = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setShowForm(false);
  };

  const closePanel = () => {
    resetForm();
    setOpen(false);
  };

  const startCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, date: filtersStore.startDate || '' });
    setFormError('');
    setShowForm(true);
  };

  const startEdit = (event) => {
    setEditingId(event.id);
    setForm({ title: event.title, date: event.date, description: event.description || '' });
    setFormError('');
    setShowForm(true);
  };

  const saveEvent = async (event) => {
    event.preventDefault();
    setFormError('');
    try {
      const saved = await timelineEventsStore.save(form, editingId);
      if (!editingId) {
        filtersStore.setSelectedTimelineEventIds([
          ...filtersStore.selectedTimelineEventIds,
          saved.id,
        ]);
      }
      resetForm();
    } catch (error) {
      setFormError(error.message || 'Не удалось сохранить событие');
    }
  };

  const deleteEvent = async (event) => {
    if (!window.confirm(`Удалить событие «${event.title}»?`)) return;
    try {
      await timelineEventsStore.remove(event.id);
      filtersStore.removeTimelineEvent(event.id);
      if (editingId === event.id) resetForm();
    } catch (_) {
      // Ошибка уже отображается в панели.
    }
  };

  return (
    <div className="timeline-events-filter">
      <div className="timeline-events-filter__selected">
        {selectedEvents.map((event) => (
          <span key={event.id} className={`tag-style timeline-event-tag ${event.is_owner ? 'is-own' : 'is-other'}`}>
            <FaFlag />
            {formatRuDate(event.date)} · {event.title}
            <FaTimes
              className="close-icon-style"
              onClick={() => filtersStore.removeTimelineEvent(event.id)}
            />
          </span>
        ))}
        <button className="tag-btn-style" type="button" onClick={() => setOpen(true)}>
          <FaFlag style={{ marginRight: 8 }} />
          События{selectedEvents.length ? ` (${selectedEvents.length})` : ''}
        </button>
      </div>

      {open ? (
        <div className="modal-overlay-style" onMouseDown={(event) => {
          if (event.target === event.currentTarget) closePanel();
        }}>
          <div className="modal-window-style timeline-events-panel" role="dialog" aria-modal="true">
            <div className="modal-header-style">
              <span>События на графиках</span>
              <button className="modal-close-btn-style" type="button" onClick={closePanel}>
                <FaTimes />
              </button>
            </div>

            <button className="timeline-events-panel__create" type="button" onClick={startCreate}>
              <FaPlus /> Создать событие
            </button>

            {showForm ? (
              <form className="timeline-events-panel__form" onSubmit={saveEvent}>
                <label>
                  Название
                  <input
                    value={form.title}
                    maxLength={255}
                    onChange={(event) => setForm({ ...form, title: event.target.value })}
                    required
                  />
                </label>
                <label>
                  Дата
                  <input
                    type="date"
                    value={form.date}
                    onChange={(event) => setForm({ ...form, date: event.target.value })}
                    required
                  />
                </label>
                <label>
                  Описание
                  <textarea
                    value={form.description}
                    maxLength={4000}
                    rows={3}
                    onChange={(event) => setForm({ ...form, description: event.target.value })}
                  />
                </label>
                {formError ? <div className="timeline-events-panel__error">{formError}</div> : null}
                <div className="timeline-events-panel__form-actions">
                  <button type="button" onClick={resetForm}>Отмена</button>
                  <button type="submit" disabled={timelineEventsStore.saving}>
                    {editingId ? 'Сохранить' : 'Создать'}
                  </button>
                </div>
              </form>
            ) : null}

            {timelineEventsStore.loading ? (
              <div className="timeline-events-panel__empty">Загрузка событий…</div>
            ) : (
              <>
                <EventList
                  title="Мои события"
                  events={timelineEventsStore.myEvents}
                  selectedIds={filtersStore.selectedTimelineEventIds}
                  onToggle={(id) => filtersStore.toggleTimelineEvent(id)}
                  onEdit={startEdit}
                  onDelete={deleteEvent}
                />
                <EventList
                  title="События других пользователей"
                  events={timelineEventsStore.otherEvents}
                  selectedIds={filtersStore.selectedTimelineEventIds}
                  onToggle={(id) => filtersStore.toggleTimelineEvent(id)}
                  onEdit={startEdit}
                  onDelete={deleteEvent}
                />
              </>
            )}
            {timelineEventsStore.error && !formError ? (
              <div className="timeline-events-panel__error">{timelineEventsStore.error}</div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
});

export default TimelineEventsPanel;
